import { useState } from 'react';
import Logo from '@/components/Logo';
import { useLogout } from '@/lib/session';
import { useDebounced } from '@/lib/catalogue';
import { useCustomers } from '@/lib/orders';
import type { MeResponse } from '@/lib/api';
import { cn } from '@/lib/utils';
import Claims from './admin/Claims';
import RewardBalance from './admin/RewardBalance';
import CouponsClaims from './CouponsClaims';

const TABS = [
  { key: 'remise', label: 'Remise des cadeaux' },
  { key: 'solde', label: 'Solde des cadeaux' },
  { key: 'coupons', label: 'Coupons de tirage' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/**
 * Espace du rôle « comptoir » : distribution des cadeaux. On cherche le client UNE
 * SEULE FOIS ; ensuite les trois onglets (Remise / Solde / Coupons) portent tous
 * sur ce client sans avoir à le ressaisir en changeant de page.
 *  - Remise : remettre ses cadeaux gagnés (Claims).
 *  - Solde  : le solde par fournisseur et ce qu'il reste avant chaque cadeau.
 *  - Coupons : les coupons de tirage dus / remis.
 */
export default function Comptoir({ session }: { session: MeResponse }) {
  const logout = useLogout();
  const [tab, setTab] = useState<TabKey>('remise');
  // Client sélectionné, partagé par les trois onglets.
  const [customer, setCustomer] = useState<{ id: number; name: string; custNum: string } | null>(
    null,
  );
  const [q, setQ] = useState('');
  const debounced = useDebounced(q, 250);
  const recherche = debounced.trim() !== '';
  const clients = useCustomers(debounced, recherche);

  return (
    <div className="min-h-full">
      <header className="bg-card border-b">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 p-4">
          <div className="flex items-center gap-4">
            <Logo className="h-10" />
            <div>
              <h1 className="text-xl font-bold">Comptoir cadeaux</h1>
              <p className="text-muted-foreground text-sm">{session.user.fullName}</p>
            </div>
          </div>
          <button
            onClick={() => logout.mutate()}
            className="border-input h-10 rounded-lg border px-4 text-sm font-medium"
          >
            Déconnexion
          </button>
        </div>
        {/* Onglets actifs seulement une fois un client choisi. */}
        {customer && (
          <nav className="mx-auto flex max-w-4xl gap-1 px-4">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={cn(
                  'border-b-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors',
                  tab === t.key
                    ? 'border-primary text-foreground'
                    : 'text-muted-foreground border-transparent hover:text-foreground',
                )}
              >
                {t.label}
              </button>
            ))}
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-4xl p-6">
        {customer === null ? (
          // Recherche UNIQUE du client, partagée ensuite par les trois onglets.
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Rechercher un client</h2>
              <p className="text-muted-foreground text-sm">
                Trouvez le client une seule fois, puis consultez ses cadeaux, son solde et ses
                coupons sans avoir à le ressaisir.
              </p>
            </div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nom, n° de client ou ville…"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
              className="border-input bg-background h-12 w-full max-w-lg rounded-lg border px-3 text-base"
            />

            {recherche && (
              <div className="grid max-w-lg gap-2">
                {clients.data?.customers.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setCustomer({ id: c.id, name: c.name, custNum: c.custNum })}
                    className="bg-card hover:border-primary/50 flex items-center gap-3 rounded-lg border p-3 text-left"
                  >
                    <span className="bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-mono text-xs font-medium">
                      {c.custNum}
                    </span>
                    <span className="font-medium">{c.name}</span>
                  </button>
                ))}
                {!clients.isPending && clients.data?.customers.length === 0 && (
                  <p className="text-muted-foreground text-sm">Aucun client trouvé.</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            {/* Bandeau client partagé par les trois onglets. */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">{customer.name}</h2>
                <p className="text-muted-foreground font-mono text-sm">{customer.custNum}</p>
              </div>
              <button
                onClick={() => {
                  setCustomer(null);
                  setQ('');
                }}
                className="border-input rounded-lg border px-4 py-2 text-sm font-medium"
              >
                ← Autre client
              </button>
            </div>

            {tab === 'remise' ? (
              <Claims customerId={customer.id} />
            ) : tab === 'solde' ? (
              <RewardBalance customerId={customer.id} />
            ) : (
              <CouponsClaims customerId={customer.id} />
            )}
          </div>
        )}
      </main>
    </div>
  );
}
